import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { MembershipTypeStore, NavigationItemStore, PageStore } from '@seedlang/stores';
import { computed } from 'mobx';
import { AppUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import { isPresent, isBlank } from '@seedlang/utils';
import NoAccessButton from 'components/worksheet/no_access_button';
import Link from 'components/link';
import PageTitle from 'components/common/page_title';
import PageViewAsSelect from 'components/pages/page_view_as_select';
import eventParamsHelper from 'utils/event_params_helper';
import { getPageViewAsSelection } from 'utils/page_view_as_storage';
import withRouteParams from 'utils/with_route_params';

const Wrapper = styled.div`
    margin-top: ${props => props.marginTop};
    margin-bottom: ${props => props.marginBottom};
    height: ${props => props.hasAccess ? null : '100%'};
    display: ${props => props.hasAccess ? null : 'flex'};
    align-items: ${props => props.hasAccess ? null : 'center'};
    justify-content: ${props => props.hasAccess ? null : 'center'};
    padding: 10px 20px;
    background: ${props => props.transparentBackground ? 'transparent' : '#FFF'};
    border-radius: ${props => props.borderRadius};
    h1,h2 {
      line-height: 32px;
    }
    li {
      margin-left: 20px;
      list-style-type: disc;
    }
    a {
      text-decoration: underline;
      word-break: break-word;
    }
    a:hover {
      text-decoration: none;
    }
    p {
      margin: 10px 0!important;
    }
    img {
      max-width: 100%;
      height: auto;
    }
    .no-access-content {
      max-width: 500px;
      text-align: center;
    }
`;

const AdminLink = styled.div`
  font-size: 11px;
  text-align: center;
  padding: 10px 5px 0 5px;
`;
@observer
class PageShow extends React.Component {
  lastTrackedPageId = null;

  componentDidMount() {
    PageStore.getIndex();
    NavigationItemStore.getIndex();
    if (AppUI.userIsGroupAdmin) {
      MembershipTypeStore.getIndex();
    }
    this.getPage();
  }

  componentDidUpdate(prevProps) {
    if (this.props.routeParams.pageId !== prevProps.routeParams.pageId) {
      this.getPage();
    }

    if (this.page?.id && this.page.id !== this.lastTrackedPageId) {
      this.lastTrackedPageId = this.page.id;
      AppUI.createEvent('memrev - view page', eventParamsHelper({
        page_name: this.page.title,
      }));
    }
  }

  componentWillUnmount() {
    PageStore.clearShowData();
  }

  @computed get page() {
    return PageStore.showData;
  }

  getPage = () => {
    PageStore.getShow({ ids: { pageId: this.props.routeParams.pageId || 'home' }});
  }

  @computed get hasAccess() {
    if (isBlank(this.page)) { return false; }
    if (this.page.membershipTypePages.length === 0 && this.page.showToNonMembers) { return true; }
    return AppUI.userIsGroupAdmin || (!AppUI.user?.isMember && this.page.showToNonMembers) || (AppUI.user.isMember && this.pageHasUserMembershipType);
  }

  @computed get pageHasUserMembershipType() {
    return isPresent(this.page) && this.page.membershipTypes.map(item => item.id).includes(AppUI.user.currentUserGroup?.membershipTypeId);
  }

  @computed get navigationItems() {
    if (!this.page?.id) { return []; }

    return NavigationItemStore.indexData?.filter(item =>
      item.signedOutPageId === this.page.id ||
      item.signedInPageId === this.page.id ||
      item.navigationItemMembershipTypes?.some(setting => setting.pageId === this.page.id),
    );
  }

  @computed get navigationItem() {
    const storedSelection = getPageViewAsSelection();
    const contextualNavigationItemId = AppUI.clickedLinkId || storedSelection.navigationItemId;
    const contextualNavigationItem = this.navigationItems
      .find(item => item.id === contextualNavigationItemId);

    if (contextualNavigationItem) { return contextualNavigationItem; }

    const navigationItemForStoredView = this.navigationItems.find(item => {
      if (storedSelection.value === 'signed-out') {
        return item.signedOutPageId === this.page.id;
      }
      if (storedSelection.value === 'signed-in-without-membership') {
        return item.signedInPageId === this.page.id;
      }

      const membershipTypeId = storedSelection.value?.replace('membership-type:', '');
      return item.navigationItemMembershipTypes?.some(setting =>
        setting.membershipTypeId === membershipTypeId && setting.pageId === this.page.id,
      );
    });

    return navigationItemForStoredView || this.navigationItems[0] || null;
  }

  @computed get showViewAsSelect() {
    return AppUI.userIsGroupAdmin &&
      MembershipTypeStore.initialRequestReceived &&
      !MembershipTypeStore.hasOutstandingRequests &&
      PageStore.initialRequestReceived &&
      !PageStore.hasOutstandingRequests &&
      NavigationItemStore.initialRequestReceived &&
      !NavigationItemStore.hasOutstandingRequests &&
      isPresent(this.navigationItem);
  }

  render() {
    return (
      <>
        <PageTitle>{this.page?.title}</PageTitle>
        {
          this.showViewAsSelect &&
            <PageViewAsSelect
              key={this.navigationItem.id}
              membershipTypes={MembershipTypeStore.indexData ?? []}
              navigationItem={this.navigationItem}
              page={this.page}
              pages={PageStore.indexData ?? []}
            />
        }
        <Wrapper
          marginTop={AppUI.layout.isMobile ? '15px' : null}
          marginBottom={AppUI.layout.isMobile ? '60px' : null}
          borderRadius={AppUI.layout.isMobile ? '0' : '10px'}
          hasAccess={this.hasAccess}
          transparentBackground={this.page.transparentBackground}
        >
          {
            PageStore.requestCounter > 0 &&
            <Spinner background={AppUI.site.accentColor} />
          }
          {
            !this.hasAccess && isPresent(this.page.membershipTypes) &&
              <div className='no-access-content'>
                <h3>Sorry, you don't have access to this page!</h3>
                <p>Please upgrade your membership level to access this page.</p>
                <NoAccessButton />
              </div>
          }
          {
            this.page.content &&
              <div
                className='page-content'
                dangerouslySetInnerHTML={{ __html: (this.hasAccess && this.page.content) || '' }}
              />
          }
          {
            AppUI.userIsGroupAdmin &&
              <AdminLink>
                {
                  isPresent(this.page) && isPresent(AppUI.site?.groupId) &&
                    <Link
                      className='underline'
                      style={{lineHeight: '10px'}}
                      to={{name: 'creator.site_pages.edit', params: {pageId: this.page?.id, groupId: AppUI.site.groupId}}}
                    >
                      Edit Page
                    </Link>
                }
              </AdminLink>
          }
        </Wrapper>
      </>
    );
  }
}

export default withRouteParams(PageShow);
